import { useEffect, useMemo, useRef, type MutableRefObject } from 'react';
import type * as THREE from 'three';
import { describeParcelRing, formatAcres, formatAcresFromSquareFeet, formatFeet, formatSquareFeet, projectParcelRing, projectToScreen } from '../utils/measureUtils';

export type ParcelFrameUpdate = (camera: THREE.Camera, canvas: HTMLCanvasElement) => void;

export default function ParcelDimensionsOverlay({ ring, frameRef, listedAreaSqFt }: {
  ring: [number, number, number][];
  frameRef: MutableRefObject<ParcelFrameUpdate | null>;
  listedAreaSqFt?: number;
}) {
  const metrics = useMemo(() => describeParcelRing(ring), [ring]);
  const root = useRef<HTMLDivElement>(null);
  const outline = useRef<SVGPolygonElement>(null);
  const corners = useRef<(SVGCircleElement | null)[]>([]);
  const edgeLabels = useRef<(HTMLDivElement | null)[]>([]);
  const areaLabel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const update: ParcelFrameUpdate = (camera, canvas) => {
      const projected = projectParcelRing(metrics.points, camera, canvas);
      const container = root.current;
      if (!container) return;
      container.style.visibility = projected ? 'visible' : 'hidden';
      container.style.display = projected ? '' : 'none';
      if (!projected) return;
      const rect = canvas.getBoundingClientRect();
      outline.current?.setAttribute('points', projected.map(p => `${p.x + rect.left},${p.y + rect.top}`).join(' '));
      projected.forEach((p, i) => {
        corners.current[i]?.setAttribute('cx', String(p.x + rect.left));
        corners.current[i]?.setAttribute('cy', String(p.y + rect.top));
        const label = edgeLabels.current[i];
        const midpoint = projectToScreen(metrics.edges[i].midpoint, camera, canvas);
        if (label) {
          label.style.visibility = midpoint.visible ? 'visible' : 'hidden';
          label.style.translate = `${midpoint.x + rect.left}px ${midpoint.y + rect.top}px`;
        }
      });
      if (areaLabel.current) {
        const label = areaLabel.current;
        const xs = projected.map(point => point.x), ys = projected.map(point => point.y);
        const minX = Math.min(...xs), maxX = Math.max(...xs);
        const minY = Math.min(...ys), maxY = Math.max(...ys);
        const width = label.offsetWidth, height = label.offsetHeight, gap = 14, inset = 8;
        const midX = (minX + maxX) / 2, midY = (minY + maxY) / 2;
        const candidates = [
          { x: maxX + gap, y: midY - height / 2 },
          { x: minX - width - gap, y: midY - height / 2 },
          { x: midX - width / 2, y: minY - height - gap },
          { x: midX - width / 2, y: maxY + gap },
        ];
        const position = candidates.find(({ x, y }) => x >= inset && y >= inset
          && x + width <= canvas.clientWidth - inset && y + height <= canvas.clientHeight - inset);
        const fallback = candidates[2];
        const x = position?.x ?? Math.min(Math.max(fallback.x, inset), Math.max(inset, canvas.clientWidth - width - inset));
        const y = position?.y ?? Math.min(Math.max(fallback.y, inset), Math.max(inset, canvas.clientHeight - height - inset));
        label.style.transform = 'none';
        label.style.visibility = 'visible';
        label.style.translate = `${x + rect.left}px ${y + rect.top}px`;
      }
    };
    frameRef.current = update;
    return () => { if (frameRef.current === update) frameRef.current = null; };
  }, [metrics, frameRef]);

  return <div ref={root} className="pointer-events-none fixed inset-0 z-[1001]" style={{ visibility: 'hidden' }} aria-label="Approximate parcel measurements">
    <svg className="absolute inset-0 h-full w-full" aria-hidden="true">
      <polygon ref={outline} fill="rgba(185,237,218,0.025)" stroke="rgba(158,216,191,0.55)" strokeWidth="1.5" strokeLinejoin="round" />
      {metrics.points.map((_, i) => <circle key={i} ref={el => { corners.current[i] = el; }} r="3" fill="rgba(185,237,218,0.75)" stroke="rgba(158,216,191,0.4)" strokeWidth="1" />)}
    </svg>
    {metrics.edges.map((edge, i) => <div key={i} ref={el => { edgeLabels.current[i] = el; }}
      className="fixed left-0 top-0 rounded-lg border border-emerald-200/20 bg-[#111827]/95 px-2 py-1 text-[10px] font-medium text-emerald-100/80 shadow-lg backdrop-blur-md"
      style={{ transform: 'translate(-50%, -50%)' }}>
      {formatFeet(edge.length)}
    </div>)}
    <div ref={areaLabel} className="fixed left-0 top-0 rounded-lg border border-emerald-200/25 bg-[#111827]/95 px-3 py-2 text-white shadow-xl backdrop-blur-md">
      <div className="text-[10px] font-semibold text-emerald-100/80">{listedAreaSqFt != null ? 'Listed lot size' : 'Approx. parcel area'}</div>
      <div className="mt-1 text-sm font-medium text-white/85">{listedAreaSqFt != null ? `${listedAreaSqFt.toLocaleString('en-US')} sq ft` : formatSquareFeet(metrics.area)}</div>
      <div className="text-[10px] text-emerald-100/70">{listedAreaSqFt != null ? formatAcresFromSquareFeet(listedAreaSqFt) : formatAcres(metrics.area)}</div>
      <div className="mt-1 text-[9px] text-white/40">Not a surveyed boundary</div>
    </div>
  </div>;
}
